import { useCallback, useEffect, useRef, useState } from "react";
import { buildApiUrl } from "../utils/ApiConfig";
import { loadTurnstile } from "../utils/turnstileLoader";

export type GateStatus = "checking" | "challenging" | "allowed" | "blocked";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

/**
 * How long the challenge may run before we tell the user it is taking a while.
 * This only surfaces a hint - it deliberately does NOT release the map.
 *
 * There is no blanket "give up and show the map" timer. An earlier version had
 * one at 10s, which meant a headless scraper got a billed map load simply by
 * waiting it out: it never solved the challenge, never called verify, and the
 * map loaded anyway. Failing open is correct when Cloudflare cannot be reached
 * at all, and only then - see the catch around loadTurnstile below.
 */
const SLOW_CHALLENGE_MS = 8_000;

/**
 * Module scope so the result survives remounts. Router renders <App> for a
 * fall-through route group, so navigating to /about and back remounts the gate;
 * without this the user would be re-challenged mid-session.
 */
let sessionAllowed = SITE_KEY === "";
let inFlightToken: string | null = null;
let inFlightVerify: Promise<boolean> | null = null;

/**
 * Single-flight per token. StrictMode double-invokes effects in dev, and
 * Turnstile tokens are single-use - POSTing the same token twice makes the
 * second call return `timeout-or-duplicate` and would block a legitimate user.
 */
function verifyToken(token: string): Promise<boolean> {
  if (inFlightToken === token && inFlightVerify) return inFlightVerify;

  inFlightToken = token;
  inFlightVerify = (async () => {
    try {
      const response = await fetch(buildApiUrl("api/turnstile/verify"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      return data.success === true;
    } catch {
      // Our own backend is unreachable - fail open rather than hide the map.
      return true;
    }
  })();

  return inFlightVerify;
}

export function useTurnstileGate() {
  const [status, setStatus] = useState<GateStatus>(
    sessionAllowed ? "allowed" : "checking"
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const settledRef = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (sessionAllowed) {
      setStatus("allowed");
      return;
    }

    let cancelled = false;
    settledRef.current = false;
    setSlow(false);

    const allow = () => {
      if (cancelled) return;
      sessionAllowed = true;
      settledRef.current = true;
      setStatus("allowed");
    };

    const block = () => {
      if (cancelled) return;
      settledRef.current = true;
      setStatus("blocked");
    };

    // Surfaces a hint only. Never releases the map.
    const slowTimer = window.setTimeout(() => {
      if (!settledRef.current && !cancelled) setSlow(true);
    }, SLOW_CHALLENGE_MS);

    void (async () => {
      // One round trip covers both the runtime kill switch and any existing
      // session cookie, which the backend validates server-side.
      try {
        const response = await fetch(buildApiUrl("api/turnstile/status"), {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) return allow();
        const { enabled, verified } = await response.json();
        if (!enabled || verified) return allow();
      } catch {
        return allow();
      }

      if (cancelled) return;
      setStatus("challenging");

      let turnstile;
      try {
        turnstile = await loadTurnstile();
      } catch {
        // Blocked by an ad blocker, or Cloudflare is down.
        return allow();
      }
      if (cancelled || !containerRef.current) return;

      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        appearance: "interaction-only",
        execution: "render",
        action: "map_load",
        theme: "auto",
        callback: (token: string) => {
          void verifyToken(token).then((ok) => (ok ? allow() : block()));
        },
        // The widget loaded, so these are challenge-level outcomes, not an
        // unreachable Cloudflare. Releasing the map here would hand a free
        // map load to anything that simply declines to solve the challenge.
        "error-callback": () => block(),
        // The challenge expired before it was solved; give the visitor a fresh
        // one rather than stranding them.
        "timeout-callback": () => {
          if (!cancelled && widgetIdRef.current) {
            try {
              turnstile.reset(widgetIdRef.current);
            } catch {
              block();
            }
          }
        },
        "expired-callback": () => {
          if (!cancelled && widgetIdRef.current) {
            try {
              turnstile.reset(widgetIdRef.current);
            } catch {
              /* the visitor is already verified, or retry is available */
            }
          }
        },
      });
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(slowTimer);
      if (widgetIdRef.current) {
        try {
          window.turnstile?.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, [attempt]);

  const retry = useCallback(() => {
    inFlightToken = null;
    inFlightVerify = null;
    setSlow(false);
    setStatus("checking");
    setAttempt((n) => n + 1);
  }, []);

  return { status, slow, containerRef, retry };
}
